import React, { useEffect, useState } from 'react';
import { professorService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ProfessorProfile {
  id: number;
  user_id: number;
  current_stage: string;
  updated_at?: string;
}

const ProfessorPathway: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfessorProfile | null>(null);
  const [stages, setStages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'professor') {
      navigate('/student-pathway');
      return;
    }
    load();
  }, [user, navigate]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await professorService.getOnboarding();
      setProfile(data.profile);
      setStages(data.stages);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load professor onboarding');
    } finally {
      setLoading(false);
    }
  };

  const advance = async () => {
    if (!profile) return;
    try {
      setAdvancing(true);
      const res = await professorService.advanceOnboarding();
      setProfile(res.profile);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to advance');
    } finally {
      setAdvancing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
  }
  if (!profile) {
    return <div className="min-h-screen flex items-center justify-center">No profile</div>;
  }

  const current = stages.find(s => s.code === profile.current_stage);
  const isFinal = !current?.nextStage;

  return (
    <div className="min-h-screen bg-gray-50 p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-2">Professor Pathway</h1>
      <p className="text-gray-600 mb-6">Track your onboarding and engagement stages.</p>

      <div className="grid gap-4 md:grid-cols-2">
        {stages.map(stage => {
          const active = stage.code === profile.current_stage;
          return (
            <div key={stage.code} className={`p-4 rounded border ${active ? 'border-indigo-600 bg-white shadow' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-xl font-semibold">{stage.title}</h2>
                {active && <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded">Current</span>}
              </div>
              <p className="text-sm text-gray-600 mb-2">{stage.description}</p>
              <div className="text-xs text-gray-500">Business: {stage.businessTitle}</div>
              {stage.nextStage && <div className="text-xs text-gray-500 mt-1">Next: {stage.nextStage.replace('_',' ')}</div>}
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex items-center gap-4">
        <button disabled={isFinal || advancing} onClick={advance} className="px-5 py-2 rounded bg-indigo-600 text-white disabled:opacity-50">{isFinal ? 'Completed' : (advancing ? 'Advancing...' : 'Advance Stage')}</button>
        <div className="text-sm text-gray-500">Current stage: {profile.current_stage.replace('_',' ')}</div>
      </div>
    </div>
  );
};

export default ProfessorPathway;
